<script setup lang="ts">
import { Button } from '~ds/components/ui/button';
import { Text } from '~ds/components/ui/typography';
import { useMotionPresets } from '~kui/composables/useMotionPresets';
import { AnimatePresence, motion } from 'motion-v';
import { useI18n } from 'vue-i18n';

import type { FeedbackSurveyQuestion, FeedbackSurveyQuestionDropdown, FeedbackSurveyQuestionScale } from './types';

import KUIUniCompletionFeedbackSurveyDropdown from './KUIUniCompletionFeedbackSurveyDropdown.vue';
import KUIUniCompletionFeedbackSurveyLikert from './KUIUniCompletionFeedbackSurveyLikert.vue';
import KUIUniCompletionFeedbackSurveyScale from './KUIUniCompletionFeedbackSurveyScale.vue';
import KUIUniCompletionFeedbackSurveyText from './KUIUniCompletionFeedbackSurveyText.vue';
import KUIUniCompletionFeedbackSurveyTextarea from './KUIUniCompletionFeedbackSurveyTextarea.vue';
import KUIUniCompletionFeedbackSurveyYesNo from './KUIUniCompletionFeedbackSurveyYesNo.vue';

const props = defineProps<{
  questions: FeedbackSurveyQuestion[];
  submitting?: boolean;
  /**
   * A failed submission, worded for the student. Rendered as a role="alert" INSIDE this
   * component because the survey lives in a credenza that marks the rest of the page
   * aria-hidden — a toast fired out there is visible to sighted users and silent to a screen
   * reader. In here, it is announced.
   */
  errorMessage?: string | null;
  /**
   * Whether a named human other than Year13 reads these answers. True for a lesson's instrument,
   * where the teacher who set the lesson sees each response beside the student's name, so the
   * student is told before they answer rather than after.
   */
  sharedWithTeacher?: boolean;
  /**
   * The teacher sees these answers without knowing who gave them, so the disclosure says so.
   * Still shown rather than dropped: that the teacher reads them at all is the part a student
   * needs before answering.
   */
  anonymousResponses?: boolean;
}>();

const emit = defineEmits<{
  submitted: [responses: Record<string, unknown>];
  skipped: [answeredCount: number];
  abandoned: [answeredCount: number];
}>();

const { t } = useI18n();
const { TRANSITION } = useMotionPresets();

const currentIndex = ref(0);
const answers = ref<Record<string, unknown>>({});

const currentQuestion = computed(() => props.questions[currentIndex.value] ?? null);
const isLast = computed(() => currentIndex.value === props.questions.length - 1);

const currentAnswer = computed(() =>
  currentQuestion.value ? answers.value[currentQuestion.value.key] : undefined,
);

// The text inputs re-emit '' when a student clears them, so a key existing is not the same as it
// being answered. One predicate, used by both the advance gate and the skip payload.
const isAnswered = (value: unknown) => value !== undefined && value !== null && value !== '';

const canAdvance = computed(() => {
  if (!currentQuestion.value) {
    return false;
  }
  if (!currentQuestion.value.required) {
    return true;
  }
  return isAnswered(currentAnswer.value);
});

const setAnswer = (key: string, value: unknown) => {
  answers.value = { ...answers.value, [key]: value };
};

const handleNext = () => {
  if (!isLast.value && canAdvance.value) {
    currentIndex.value++;
  }
};

const scaleQuestion = computed(() => currentQuestion.value as FeedbackSurveyQuestionScale);
const dropdownQuestion = computed(() => currentQuestion.value as FeedbackSurveyQuestionDropdown);
const scaleModel = computed(() => currentAnswer.value as number | undefined);
const booleanModel = computed(() => currentAnswer.value as boolean | undefined);
const dropdownModel = computed(() => currentAnswer.value as string | number | null | undefined);
const stringNumberModel = computed(() => currentAnswer.value as string | number);
const stringModel = computed(() => currentAnswer.value as string | undefined);

const answeredCount = () => Object.values(answers.value).filter(isAnswered).length;

// Leaving writes nothing, however the student leaves. The responses table is unique on
// (collection, user) and first write wins, so a "skipped" row would lock the student out of ever
// answering; the count of what they had filled in goes to analytics instead, which is the only
// thing that wanted it.
//
// One latch for both ways out, because the skip button stays hit-testable through the step's 200ms
// exit animation and the parent advances a step per emit — a double tap would skip the next reward
// screen outright — and because a survey already dealt with must not also report itself abandoned.
const settled = ref(false);

const handleSubmit = () => {
  if (canAdvance.value && !settled.value) {
    settled.value = true;
    emit('submitted', { ...answers.value });
  }
};

// A failed submission un-latches the survey: the parent reporting an error means nothing was
// dealt with, and a latch left set would strand the student — Submit inert, Skip disabled,
// answers unrecoverable — behind an alert telling them something went wrong.
watch(() => props.errorMessage, (message) => {
  if (message) {
    settled.value = false;
  }
});

// A survey a teacher switched on is answered or not started, never abandoned halfway: a partial
// set is what makes an Impact Report awkward to read. The free-text questions stay optional, but
// they do that by not being `required`, which lets a student pass them without leaving the survey.
const canSkip = computed(() => !props.sharedWithTeacher);

const handleSkip = () => {
  if (settled.value) {
    return;
  }
  settled.value = true;

  emit('skipped', answeredCount());
};

// The student who walks out rather than finishing: the close button in the corner tears down the
// whole completion screen from wherever they are, and a lesson survey has no skip button to take
// instead. Reported from here because this is the only place that knows both that the survey was
// the thing on screen and how far they had got through it.
//
// Deliberately not the 'skipped' emit, which the parent answers by advancing a step. There is no
// step left to advance to once this is unmounting.
onUnmounted(() => {
  if (settled.value) {
    return;
  }

  emit('abandoned', answeredCount());
});
</script>

<template>
  <div class="flex h-full w-full flex-col items-center justify-center gap-8">
    <Text
      v-if="sharedWithTeacher"
      size="sm"
      class="absolute inset-x-0 top-0 z-20 px-6 pt-4 text-center text-muted-foreground"
      data-testid="feedback-survey-teacher-disclosure"
    >
      {{ anonymousResponses
        ? t('unified.activity.completion.feedbackSurvey.teacherDisclosureAnonymous')
        : t('unified.activity.completion.feedbackSurvey.teacherDisclosure') }}
    </Text>

    <AnimatePresence mode="wait">
      <motion.div
        v-if="currentQuestion"
        :key="currentQuestion.key"
        :initial="{ opacity: 0, x: 40 }"
        :animate="{ opacity: 1, x: 0 }"
        :exit="{ opacity: 0, x: -40 }"
        :transition="{ ...TRANSITION, duration: 0.3 }"
        class="flex w-full flex-col items-center justify-center"
      >
        <KUIUniCompletionFeedbackSurveyScale
          v-if="currentQuestion.type === 'SCALE'"
          :question="scaleQuestion"
          :model-value="scaleModel"
          @update:model-value="setAnswer(currentQuestion.key, $event)"
        />
        <KUIUniCompletionFeedbackSurveyYesNo
          v-else-if="currentQuestion.type === 'YES_NO'"
          :question="currentQuestion"
          :model-value="booleanModel"
          @update:model-value="setAnswer(currentQuestion.key, $event)"
        />
        <KUIUniCompletionFeedbackSurveyDropdown
          v-else-if="currentQuestion.type === 'DROPDOWN'"
          :question="dropdownQuestion"
          :model-value="dropdownModel"
          @update:model-value="setAnswer(currentQuestion.key, $event)"
        />
        <KUIUniCompletionFeedbackSurveyText
          v-else-if="currentQuestion.type === 'TEXT'"
          :question="currentQuestion"
          :model-value="stringNumberModel"
          @update:model-value="setAnswer(currentQuestion.key, $event)"
        />
        <KUIUniCompletionFeedbackSurveyTextarea
          v-else-if="currentQuestion.type === 'TEXTAREA'"
          :question="currentQuestion"
          :model-value="stringModel"
          @update:model-value="setAnswer(currentQuestion.key, $event)"
        />
        <KUIUniCompletionFeedbackSurveyLikert
          v-else-if="currentQuestion.type === 'LIKERT'"
          :question="currentQuestion"
          :model-value="stringModel"
          @update:model-value="setAnswer(currentQuestion.key, $event)"
        />
      </motion.div>
    </AnimatePresence>

    <div class="absolute inset-x-0 bottom-2 md:bottom-6 z-20 flex flex-col items-center px-4 sm:px-6">
      <div
        v-if="errorMessage"
        role="alert"
        class="mb-3 max-w-sm w-full rounded-lg bg-white border border-destructive text-destructive text-sm px-4 py-2 text-center"
        data-testid="feedback-survey-error"
      >
        {{ errorMessage }}
      </div>
      <Button
        v-if="!isLast"
        size="lg"
        class="max-w-sm text-xl md:text-2xl w-full"
        :disabled="!canAdvance"
        data-testid="feedback-survey-next-button"
        type="button"
        @click="handleNext"
      >
        {{ t('unified.common.next') }}
      </Button>
      <Button
        v-else
        size="lg"
        class="max-w-sm text-xl md:text-2xl w-full"
        :disabled="!canAdvance"
        :loading="submitting"
        data-testid="feedback-survey-submit-button"
        type="button"
        @click="handleSubmit"
      >
        {{ t('unified.common.submit') }}
      </Button>
      <Button
        v-if="canSkip"
        variant="ghost"
        size="sm"
        class="mt-2"
        :disabled="submitting || settled"
        data-testid="feedback-survey-skip-button"
        type="button"
        @click="handleSkip"
      >
        {{ t('unified.activity.completion.feedbackSurvey.skip') }}
      </Button>
    </div>
  </div>
</template>
